import { useState } from "react";
import { Copy } from "lucide-react";
import type { InvitationResult } from "../../backend/api/users";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";

export function InvitationNotice({ invitation }: { invitation: InvitationResult }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const signInUrl = invitation.sign_in_url || `${window.location.origin}/login`;
  const expiry = invitation.temporary_password_expires_at ? new Date(invitation.temporary_password_expires_at).toLocaleString() : "See the invitation expiry";
  const instructions = invitation.temporary_password ? [
    "Your CT Relocation Portal account is ready.",
    `Email: ${invitation.email}`,
    `Temporary password: ${invitation.temporary_password}`,
    `Sign in: ${signInUrl}`,
    invitation.invitation_url ? `Invitation: ${invitation.invitation_url}` : "",
    "1. Open the sign-in page, enter your email and select Next.",
    "2. Enter the temporary password above and select Sign in.",
    "3. Choose your own password before entering the portal.",
    `Temporary password expires: ${expiry}. It stops working after password setup or a replacement invitation.`,
  ].filter(Boolean).join("\n") : invitation.invitation_url;

  async function copyLink() {
    if (!instructions) return;
    try {
      await navigator.clipboard.writeText(instructions);
      setCopied(true);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  }

  return (
    <Alert>
      <AlertTitle>{invitation.delivered ? "Invitation emailed" : "Invitation ready to share"}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{invitation.delivered ? `An invitation was emailed to ${invitation.email}.` : invitation.reason}</p>
        {invitation.temporary_password ? <>
          <dl className="grid gap-1 text-sm"><dt>Email</dt><dd>{invitation.email}</dd><dt>Temporary password</dt><dd><code className="break-all">{invitation.temporary_password}</code></dd></dl>
          <ol className="list-decimal space-y-1 pl-5"><li>Open <a href={signInUrl} className="underline">the sign-in page</a>, enter your email and select Next.</li><li>Enter the temporary password above and select Sign in.</li><li>Set a new password before entering the portal.</li></ol>
          <p>Copy these instructions now. The temporary password is shown only in this invitation response and is replaced when another invitation is issued. Expiry: {expiry}.</p>
        </> : <p>{invitation.email} is approved for portal access. Use the invitation to choose a configured Google, Microsoft, or email verification method.</p>}
        {instructions && <>
          <div className="flex flex-wrap items-center gap-2">
            {invitation.invitation_url && <code className="max-w-full break-all rounded border bg-muted px-2 py-1 text-xs">{invitation.invitation_url}</code>}
            <Button variant="outline" size="sm" onClick={copyLink}>
              <Copy className="h-4 w-4" />{copied ? "Copied" : invitation.temporary_password ? "Copy sign-in instructions" : "Copy invitation link"}
            </Button>
          </div>
          {copied && <p role="status">{invitation.temporary_password ? "Sign-in instructions copied." : "Invitation link copied."}</p>}
          {copyError && <p role="alert">Could not copy automatically. Select and copy the details above.</p>}
        </>}
        {invitation.invitation_url && <p>This link works once and stops working if a replacement invitation is issued.</p>}
      </AlertDescription>
    </Alert>
  );
}
